import { useState, useEffect, useCallback } from 'react';
import { watchlistService, WatchlistStock } from '@services/watchlist/watchlistService';
import { useAuthStore } from '@store/authStore';

interface UseWatchlistReturn {
  watchlist: WatchlistStock[];
  isLoading: boolean;
  error: string | null;
  addToWatchlist: (ticker: string, companyName: string) => Promise<boolean>;
  removeFromWatchlist: (ticker: string) => Promise<boolean>;
  isInWatchlist: (ticker: string) => boolean;
  refreshWatchlist: () => Promise<void>;
}

export const useWatchlist = (): UseWatchlistReturn => {
  const { user } = useAuthStore();
  const [watchlist, setWatchlist] = useState<WatchlistStock[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch watchlist
  const fetchWatchlist = useCallback(async () => {
    if (!user?.id) {
      setWatchlist([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await watchlistService.getWatchlist(user.id);

      if (fetchError || !data) {
        throw new Error(fetchError || 'Failed to fetch watchlist');
      }

      setWatchlist(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('useWatchlist fetch error:', errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Add to watchlist
  const addToWatchlist = useCallback(
    async (ticker: string, companyName: string): Promise<boolean> => {
      if (!user?.id) {
        console.error('No user logged in');
        return false;
      }

      try {
        const { data, error: addError } = await watchlistService.addToWatchlist(
          user.id,
          ticker,
          companyName
        );

        if (addError || !data) {
          console.error('Failed to add to watchlist:', addError);
          return false;
        }

        // Update local state
        setWatchlist(prev => [...prev, data]);
        return true;
      } catch (err) {
        console.error('Add to watchlist error:', err);
        return false;
      }
    },
    [user?.id]
  );

  // Remove from watchlist
  const removeFromWatchlist = useCallback(
    async (ticker: string): Promise<boolean> => {
      if (!user?.id) {
        console.error('No user logged in');
        return false;
      }

      try {
        const { success, error: removeError } = await watchlistService.removeFromWatchlist(
          user.id,
          ticker
        );

        if (!success || removeError) {
          console.error('Failed to remove from watchlist:', removeError);
          return false;
        }

        // Update local state
        setWatchlist(prev => prev.filter(item => item.ticker !== ticker));
        return true;
      } catch (err) {
        console.error('Remove from watchlist error:', err);
        return false;
      }
    },
    [user?.id]
  );

  // Check if ticker is in watchlist (local check)
  const isInWatchlist = useCallback(
    (ticker: string): boolean => {
      return watchlist.some(item => item.ticker === ticker);
    },
    [watchlist]
  );

  // Refresh watchlist
  const refreshWatchlist = useCallback(async () => {
    await fetchWatchlist();
  }, [fetchWatchlist]);

  // Initial fetch on mount
  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist]);

  return {
    watchlist,
    isLoading,
    error,
    addToWatchlist,
    removeFromWatchlist,
    isInWatchlist,
    refreshWatchlist,
  };
};
